import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  startAuthorization,
  loginWithPassword,
  logout as blocksLogout,
  isAuthenticated as hasValidToken,
  getMe,
  getProfile,
} from '@/services/auth';

const AuthContext = createContext();

/** Decode a base64url-encoded JWT payload without verifying the signature. */
function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Extract a flat list of role names from claims, tolerating every shape Blocks IAM
 * (and OIDC providers) use: a flat array, a CSV/space-separated string, a Keycloak
 * `realm_access.roles` array, or — importantly — a **per-organization map** like
 * `{ "<orgId>": ["admin", "user"] }` (Blocks stores roles per org).
 */
function extractRoles(claims) {
  const sources = [
    claims.roles,
    claims.role,
    claims.Roles,
    claims.Role,
    claims.realm_access?.roles,
    claims.authorities,
    claims.groups,
  ];
  const out = [];
  for (const src of sources) {
    if (!src) continue;
    if (Array.isArray(src)) out.push(...src);
    else if (typeof src === 'string') out.push(...src.split(/[,\s]+/));
    else if (typeof src === 'object') out.push(...Object.values(src).flat()); // per-org map
  }
  return out.filter(Boolean).map(String);
}

/** Map OIDC token claims to the user shape the app expects (e.g. `user.role === 'admin'`). */
function buildUserFromClaims(claims) {
  if (!claims) return null;

  const roleList = extractRoles(claims);
  // Match 'admin', 'Admin', org-scoped 'orgadmin', slugs like 'super-admin', etc.
  const isAdmin = roleList.some((r) => r.toLowerCase().includes('admin'));

  if (import.meta.env.DEV) {
    console.debug('[auth] roles resolved:', roleList, '→ isAdmin:', isAdmin, '| claim keys:', Object.keys(claims));
  }

  const fullName =
    claims.name ||
    [claims.given_name, claims.family_name].filter(Boolean).join(' ') ||
    claims.email ||
    '';

  return {
    ...claims,
    id: claims.sub,
    email: claims.email || '',
    full_name: fullName,
    name: fullName,
    role: isAdmin ? 'admin' : roleList[0] || 'user',
    roles: roleList,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Resolve the current session. Two claim sources:
  //  1. Embedded/token login → tokens in localStorage (sync, fast path).
  //  2. Hosted OIDC (cookie) login → HTTP-only cookie, confirmed via GET /auth/me.
  // NOTE: the Blocks access token carries NO roles (only sub/org_id/tenant_id/…),
  // so roles/admin status come from the IAM profile (GET /iam/me → per-org role map).
  const loadSession = useCallback(async () => {
    setIsLoadingAuth(true);
    try {
      let claims = null;
      if (hasValidToken()) {
        // Prefer the id_token (profile claims); fall back to the access_token.
        const idToken = localStorage.getItem('id_token') || localStorage.getItem('access_token');
        claims = idToken ? decodeJwt(idToken) : null;
      } else {
        // No local token — probe for a Blocks-hosted cookie session.
        try {
          claims = await getMe();
        } catch (sessionError) {
          console.warn(
            `[auth] cookie-session check (GET /auth/me) failed: ${sessionError?.status || ''} ${sessionError?.message || sessionError}`,
          );
          claims = null;
        }
      }

      if (!claims) {
        setUser(null);
        setIsAuthenticated(false);
        setAuthError(null);
        return;
      }

      // Enrich with the IAM profile so roles (and name/email) are available —
      // roles are not in the token. GET /iam/me → { roles: { "<orgId>": [...] }, … }.
      try {
        const profile = await getProfile();
        if (profile && typeof profile === 'object') {
          claims = { ...claims, ...profile, roles: profile.roles ?? claims.roles };
        }
      } catch (profileError) {
        console.warn(
          `[auth] profile fetch (GET /iam/me) failed; roles unavailable: ${profileError?.status || ''} ${profileError?.message || profileError}`,
        );
      }

      const resolvedUser = buildUserFromClaims(claims);
      setUser(resolvedUser);
      setIsAuthenticated(true);
      setAuthError(null);

      // Cache the actor identity for audit logs. In the cookie-session flow the JWT
      // lives in an httpOnly cookie (not readable by JS), so the data layer can't
      // decode it — persist email/name/sub here so audit "changed by" is populated.
      // Cache a human-readable identity only (email/name) — never the opaque
      // `sub`/id ("blocks|<uuid>"), so audit "changed by" shows an email, not an id.
      const actor = resolvedUser?.email || resolvedUser?.name || '';
      if (actor) localStorage.setItem('actor', actor);
    } catch (error) {
      console.error('Session load failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      setAuthError({ type: 'unknown', message: error.message || 'Failed to load session' });
    } finally {
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Start the Blocks IAM (OIDC) login flow — browser redirects to the IAM page.
  const navigateToLogin = useCallback(() => {
    startAuthorization();
  }, []);

  /**
   * Embedded (password) login. Returns the discriminated result from
   * loginWithPassword so the form can render the captcha/MFA follow-up:
   *   { status: 'success' } | { status: 'mfa_required', ... } | { status: 'captcha_required', ... }
   * On success, reloads the session so the app re-renders authenticated.
   * Throws on bad credentials — let the form catch and display it.
   */
  const login = useCallback(
    async (credentials) => {
      const result = await loginWithPassword(credentials);
      if (result.status === 'success') loadSession();
      return result;
    },
    [loadSession],
  );

  const logout = useCallback(async (shouldRedirect = true) => {
    try {
      await blocksLogout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      if (shouldRedirect) window.location.replace('/');
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoadingAuth,
        authError,
        authChecked,
        login,
        logout,
        navigateToLogin,
        checkUserAuth: loadSession,
        checkAppState: loadSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
